import { defineStore } from "pinia";
import { ref } from "vue";
import {
  getFoodItems,
  getFoodItemById,
  saveFoodItem,
  updateFoodItem,
  removeFoodItem,
  getPer100gNutrition,
  type SyncableFoodItem,
} from "@/services/foodItems";
import {
  lookupFoodByBarcode,
  isNotFoundError,
  NotFoundError,
} from "@/services/barcodeLookup";
import {
  fetchSystemFoodById,
  shouldHydrateSystemFood,
  searchBackendFoods,
} from "@/services/backendFoodSearch";
import {
  searchLocalFoods,
  upsertFoodItemInLocalSearch,
  upsertRecipeInLocalSearch,
  removeLocalFoodSearchDoc,
} from "@/services/localFoodSearch";
import type { FoodItem } from "@macro-tracker/models";
import type { Recipe } from "@/services/recipes";

export type { SyncableFoodItem, FoodItem };
export { isNotFoundError, NotFoundError, shouldHydrateSystemFood, getPer100gNutrition };

export const useFoodItemsStore = defineStore("foodItems", () => {
  const foodItems = ref<SyncableFoodItem[]>([]);

  function upsertInState(foodItem: SyncableFoodItem): void {
    if (foodItem.isArchived) {
      foodItems.value = foodItems.value.filter((item) => item.id !== foodItem.id);
      return;
    }

    const existingIndex = foodItems.value.findIndex((item) => item.id === foodItem.id);
    if (existingIndex === -1) {
      foodItems.value = [...foodItems.value, foodItem];
      return;
    }

    foodItems.value = foodItems.value.map((item, index) =>
      index === existingIndex ? foodItem : item,
    );
  }

  async function load(): Promise<void> {
    foodItems.value = await getFoodItems();
  }

  async function getById(id: string): Promise<SyncableFoodItem | null> {
    return getFoodItemById(id);
  }

  async function save(foodItem: SyncableFoodItem): Promise<SyncableFoodItem> {
    const saved = await saveFoodItem(foodItem);
    upsertInState(saved);
    return saved;
  }

  async function update(
    id: string,
    updates: Partial<SyncableFoodItem>,
  ): Promise<SyncableFoodItem | null> {
    const updated = await updateFoodItem(id, updates);
    if (updated) {
      upsertInState(updated);
    }
    return updated;
  }

  async function remove(id: string): Promise<boolean> {
    const removed = await removeFoodItem(id);
    if (removed) {
      foodItems.value = foodItems.value.filter((item) => item.id !== id);
    }
    return removed;
  }

  async function lookupByBarcode(upc: string): Promise<{ food: FoodItem }> {
    return lookupFoodByBarcode(upc);
  }

  async function fetchSystemFood(id: string): Promise<FoodItem | null> {
    return fetchSystemFoodById(id);
  }

  async function searchBackend(query: string): Promise<FoodItem[]> {
    return searchBackendFoods(query);
  }

  async function searchLocal(
    query: string,
    options?: { limit?: number; includeRecipes?: boolean },
  ) {
    return searchLocalFoods(query, options);
  }

  async function upsertInLocalSearch(
    food: FoodItem,
    entityType: "system-food" | "custom-food" | "shared-food",
  ): Promise<void> {
    return upsertFoodItemInLocalSearch(food, entityType);
  }

  async function upsertRecipeInSearch(
    recipe: Recipe,
    entityType: "custom-recipe" | "shared-recipe",
  ): Promise<void> {
    return upsertRecipeInLocalSearch(recipe, entityType);
  }

  async function removeFromLocalSearch(
    entityType: "system-food" | "custom-food" | "shared-food" | "custom-recipe" | "shared-recipe",
    entityId: string,
  ): Promise<void> {
    return removeLocalFoodSearchDoc(entityType, entityId);
  }

  return {
    foodItems,
    load,
    getById,
    save,
    update,
    remove,
    lookupByBarcode,
    fetchSystemFood,
    searchBackend,
    searchLocal,
    upsertInLocalSearch,
    upsertRecipeInSearch,
    removeFromLocalSearch,
  };
});
